import { useState, useEffect, useMemo, useCallback } from "react";
import { message } from "antd";
import { useSelector } from "react-redux";
import { ParticipantModal } from "./ParticipantModal";
import * as participantesService from "../../../services/participantes/participantesService";
import * as ubicacionesService from "../../../services/participantes/ubicacionesService";
import * as tiposDocumentoService from "../../../services/participantes/tiposDocumentoService";
import * as generosService from "../../../services/participantes/generosService";
import * as estadosValidacionService from "../../../services/participantes/estadosValidacionService";
import * as motivosNoElegibleService from "../../../services/participantes/motivosNoLegibleService";
import * as metricasService from "../../../services/participantes/metricasService";
import * as estadosParticipanteService from "../../../services/participantes/estadosParticipanteService";
import * as graficasService from "../../../services/participantes/graficasService";
import * as llamadasService from "../../../services/participantes/llamadasService";
import * as rolPromotoresService from "../../../services/participantes/rolPromotoresService";
import * as reprocesoService from "../../../services/participantes/reprocesoService";
import { selectAppPermissions } from "../../../store/auth/selectors";
import { ChartsSection } from "./ChartsSection";
import { ParticipantesCharts } from "./ParticipantesCharts";
import { ParticipantesTable } from "./ParticipantesTable";
import { ParticipantesFilter } from "./ParticipantesFilter";
import { CallHistoryModal } from "../Llamada/CallHistoryModal";
import { AddCallModal } from "../AddCallModal/AddCallModal";
import { ReprocesoModal } from "./ReprocesoModal";

export default function ParticipantesPage() {

  const [participants, setParticipants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterSexo, setFilterSexo] = useState("todos");
  const [filterDepartamento, setFilterDepartamento] = useState("todos");
  const [filterMunicipio, setFilterMunicipio] = useState("todos");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [showCharts, setShowCharts] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [mode, setMode] = useState("create");
  const [ip, setIp] = useState(null);
  const [loadingGraficas, setLoadingGraficas] = useState(false);
  const [datosGraficas, setDatosGraficas] = useState(null);
  const [graficasCargadas, setGraficasCargadas] = useState(false);
  const [addCallModalOpen, setAddCallModalOpen] = useState(false);
  const [callHistoryModalOpen, setCallHistoryModalOpen] = useState(false);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [promotoresId, setPromotoresId] = useState(null);
  const [promotores, setPromotores] = useState([]);
  const [loadingPromotores, setLoadingPromotores] = useState(false);
  const [reprocesoOpen, setReprocesoOpen] = useState(false);
  const [reprocesoParticipant, setReprocesoParticipant] = useState(null);

  const [maestros, setMaestros] = useState({
    motivosNoElegible: [],
    estadosValidacion: [],
    subestadosValidacion: [],
    tiposDocumento: [],
    generos: [],
    departamentos: [],
    municipios: [],
    estadosParticipante: []
  });

  const [metricas, setMetricas] = useState(null);
  const appName = "participante";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username;
  const isAdmin = user?.priv_admin === true || user?.role === "1";
  const isRol2 = user?.role === "2";
  const isRol3 = user?.role === "3";

  const filtrosActuales = useMemo(() => ({
    searchTerm,
    filterEstado,
    filterSexo,
    filterDepartamento,
    filterMunicipio,
    promotoresId
  }), [
    searchTerm,
    filterEstado,
    filterSexo,
    filterDepartamento,
    filterMunicipio,
    promotoresId
  ]);

  const userInfo = useMemo(() => ({
    usuarioId: userLogin,
    ip: ip,
    usuarioMunicipioId: user?.cod_municipio || 0,
    esAdmin: user?.priv_admin || false,
    filtros: filtrosActuales
  }), [userLogin, ip, user?.cod_municipio, user?.priv_admin, filtrosActuales]);

  const permissionInsert = permissions.priv_insert === "Y";
  const permissionDelete = permissions.priv_delete === "Y";
  const permissionUpdate = permissions.priv_update === "Y";

  const aplicarFiltrosPorRol = () => {
    if (!user) return;

    if (!isAdmin) {
      // 🔒 Usuario NO admin → valores fijos
      setFilterDepartamento(user.cod_departamento);
      setFilterMunicipio(user.cod_municipio);
    } else {
      // 🧹 Admin → limpiar dependientes
      setFilterMunicipio("todos");
      setPromotoresId(null);
      setPromotores([]);
    }
  };

  useEffect(() => {
    aplicarFiltrosPorRol();
  }, [user, isAdmin]);


  useEffect(() => {
    async function fetchIp() {
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        setIp(data.ip);
      } catch (e) {
        console.error("Error obteniendo IP:", e);
        setIp("127.0.0.1");
      }
    }
    fetchIp();
  }, []);

  useEffect(() => {
    const cargarMaestros = async () => {
      try {
        const promises = [
          motivosNoElegibleService.getMotivosNoElegible().catch(err => {
            console.warn("Error cargando motivos no elegible:", err.message);
            return { data: [] };
          }),
          estadosValidacionService.getEstadosValidacion().catch(err => {
            console.warn("Error cargando estados validación:", err.message);
            return { data: [] };
          }),
          tiposDocumentoService.getTiposDocumento().catch(err => {
            console.warn("Error cargando tipos documento:", err.message);
            return { data: [] };
          }),
          generosService.getGeneros().catch(err => {
            console.warn("Error cargando géneros:", err.message);
            return { data: [] };
          }),
          ubicacionesService.getDepartamentos(1).catch(err => {
            console.warn("Error cargando todos los departamentos:", err.message);
            return { data: [] };
          }),
          ubicacionesService.getDepartamentos(2).catch(err => {
            console.warn("Error cargando departamentos filtrados:", err.message);
            return { data: [] };
          }),
          estadosParticipanteService.getEstadosParticipante().catch(err => {
            console.warn("Error cargando estados participante:", err.message);
            return { data: [] };
          })
        ];
        const [motivosRes, estadosRes, tiposDocRes, generosRes, deptosRes, deptosFiltradosRes, estadosPartRes] =
          await Promise.all(promises);
        setMaestros(prev => ({
          ...prev,
          motivosNoElegible: motivosRes.data || [],
          estadosValidacion: estadosRes.data || [],
          tiposDocumento: tiposDocRes.data || [],
          generos: generosRes.data || [],
          departamentos: deptosRes.data || [],
          departamentosFiltrados: deptosFiltradosRes.data || [],
          estadosParticipante: estadosPartRes.data || []
        }));

      } catch (error) {
        console.error("Error general cargando de los datos:", error);
        message.error("Error cargando algunos datos");
      }
    };

    if (ip && userLogin) {
      cargarMaestros();
    }
  }, [ip, userLogin]);

  useEffect(() => {
    const cargarMetricasParticipantes = async () => {
      if (!userInfo.usuarioId || !userInfo.ip) {
        return;
      }
      try {
        const response = await metricasService.obtenerMetricasParticipantes(userInfo, filtrosActuales);
        setMetricas({
          total_participantes: response.data?.total_participantes || 0,
          total_en_proceso: response.data?.total_en_proceso || 0,
          total_verificados: response.data?.total_verificados || 0,
          total_no_verificados: response.data?.total_no_verificados || 0,
          total_no_continua: response.data?.total_no_continua || 0,
          total_cupos: response.data?.total_cupos || 0
        });
      } catch (error) {
        console.error("Error cargando métricas:", error);
        setMetricas({
          total_participantes: 0,
          total_en_proceso: 0,
          total_verificados: 0,
          total_no_verificados: 0,
          total_no_continua: 0,
          total_cupos: 0
        });
      }
    };
    cargarMetricasParticipantes();
  }, [userInfo, filtrosActuales]);

  useEffect(() => {
    const cargarDatosGraficas = async () => {
      if (!userInfo.usuarioId || !userInfo.ip) {
        return;
      }
      setLoadingGraficas(true);
      setGraficasCargadas(false);
      try {
        await new Promise(resolve => setTimeout(resolve, 100));
        const response = await graficasService.obtenerDatosGraficas(userInfo, filtrosActuales);
        if (response && response.success !== false && response.data) {
          setDatosGraficas(response.data);
        } else {
          setDatosGraficas({
            estados: [],
            subestadosPorEstado: [],
            generos: [],
            departamentos: [],
            municipiosPorDepartamento: []
          });
        }
      } catch (error) {
        console.error("Error cargando datos gráficas:", error);
        setDatosGraficas({
          estados: [],
          subestadosPorEstado: [],
          generos: [],
          departamentos: [],
          municipiosPorDepartamento: []
        });
      } finally {
        setLoadingGraficas(false);
        setGraficasCargadas(true);
      }
    };
    cargarDatosGraficas();
  }, [userInfo, filtrosActuales]);

  useEffect(() => {
    if (!isAdmin) return;
    setFilterMunicipio("todos");
    setPromotoresId(null);
    setPromotores([]);

    const cargarMunicipios = async () => {
      if (filterDepartamento && filterDepartamento !== "todos") {
        try {
          const response = await ubicacionesService.getMunicipios(
            filterDepartamento,
            2
          );
          setMaestros(prev => ({
            ...prev,
            municipios: response.data || []
          }));
        } catch (error) {
          console.error("Error cargando municipios:", error);
          setMaestros(prev => ({ ...prev, municipios: [] }));
        }
      } else {
        setMaestros(prev => ({ ...prev, municipios: [] }));
      }
    };

    cargarMunicipios();

  }, [filterDepartamento]);


  useEffect(() => {
    if (isAdmin && filterMunicipio === user?.cod_municipio) return;
    setPromotoresId(null);
    const cargarPromotores = async () => {
      if (!filterMunicipio || filterMunicipio === "todos") {
        setPromotores([]);
        return;
      }
      try {
        setLoadingPromotores(true);
        const res = await rolPromotoresService.getPromotoresByMunicipio(filterMunicipio);
        setPromotores(res?.data || []);
      } catch (error) {
        console.error("Error cargando promotores:", error);
        setPromotores([]);
      } finally {
        setLoadingPromotores(false);
      }
    };

    cargarPromotores();

  }, [filterMunicipio]);

  const loadParticipants = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        search: searchTerm,
        estado: filterEstado !== "todos" ? filterEstado : undefined,
        municipioId: filterMunicipio !== "todos" ? parseInt(filterMunicipio) : undefined,
        departamentoId: filterDepartamento !== "todos" ? parseInt(filterDepartamento) : undefined,
        genero: filterSexo !== "todos" ? filterSexo : undefined,
        promotoresId: promotoresId || undefined
      };
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      const response = await participantesService.getParticipantes(params, userInfo);
      setParticipants(response.data || []);
      setTotal(response.meta?.total || 0);
    } catch (error) {
      console.error("Error cargando participantes:", error);
      message.error("Error cargando participantes");
      setParticipants([]);
      setTotal(0);
    }
    setLoading(false);
  }, [
    page,
    pageSize,
    searchTerm,
    filterEstado,
    filterDepartamento,
    filterMunicipio,
    filterSexo,
    promotoresId,
    userInfo.usuarioId,
    userInfo.ip
  ]);

  useEffect(() => {
    loadParticipants();
  }, [
    loadParticipants
  ]);

  const normalizarParticipante = useCallback((p) => {
    const normalizarTexto = (valor, defecto = 'No reportado') => {
      if (valor === null || valor === undefined) return defecto;
      const limpio = valor.toString().trim();
      return limpio === '' ? defecto : limpio;
    };
    return {
      id: p.id,
      nombres: p.nombres,
      apellidos: p.apellidos,
      genero: p.genero,
      genero_nombre: p.genero_nombre,
      tipo_documento: p.tipo_documento,
      tipo_documento_nombre: p.tipo_documento_nombre,
      num_identificacion: p.num_identificacion,
      municipio_id: p.municipio_id,
      municipio: p.municipio,
      departamento_id: p.departamento_id,
      departamento: p.departamento,
      telefono: p.telefono,
      correo: normalizarTexto(p.correo),
      estado_participante: p.estado_participante,
      total_intentos: p.total_intentos || 0,
      estado_validacion: p.estado_validacion,
      estado_validacion_nombre: p.estado_validacion_nombre,
      subestado_validacion: p.subestado_validacion,
      subestado_validacion_nombre: p.subestado_validacion_nombre,
      fecha_ultimo_intento: p.fecha_ultimo_intento,
      fecha_verificacion: p.fecha_verificacion,
      estado_resumen: p.estado_resumen,
      dias_inactividad: p.dias_inactividad,
      nombre: p.nombres,
      apellido: p.apellidos,
      identificacion: p.num_identificacion,
      sexo: p.genero,
      status: p.estado_resumen || "NO_VERIFICADO",
      total_llamadas: p.total_intentos || 0,
      llamadas_verificadas: p.estado_validacion === 'VERIFICADO' ? 1 : 0,
      created_at: p.created_at_formateada,
      fecha_nacimiento: p.fecha_nacimiento_formateada,
      corregimiento_id: p.corregimiento_id,
      barrio_id: p.barrio_id,
      vereda_id: p.vereda_id,
      correo_act_ruv: normalizarTexto(p.correo_act_ruv),
      direccion: normalizarTexto(p.direccion),
      direccion_actual_ruv: normalizarTexto(p.direccion_actual_ruv),
      nombre_corregimiento: normalizarTexto(p.nombre_corregimiento),
      nombre_barrio: normalizarTexto(p.nombre_barrio),
      nombre_vereda: normalizarTexto(p.nombre_vereda),
      promotor_asignado: normalizarTexto(p.promotor_asignado, 'Sin asignar')

    };
  }, []);

  const handleCreate = () => {
    setSelectedParticipant(null);
    setMode("create");
    setModalOpen(true);
  };

  const handleEdit = async (p) => {
    try {
      const res = await participantesService.getParticipante(p.id, userInfo);
      const data = res.data;
      const normalizado = normalizarParticipante(data);
      setSelectedParticipant(normalizado);
      setMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error("Error cargando participante:", error);
      message.error("Error cargando participante");
    }
  };

  const handleDelete = async (id) => {
    try {
      await participantesService.deleteParticipante(id, userInfo);
      message.success("Participante eliminado");
      setParticipants((prev) => prev.filter((p) => p.id !== id));
      setTotal((prev) => prev - 1);
      await recargarDatos();
    } catch (error) {
      console.error("❌ Error eliminando participante:", error);
      message.error("No se pudo eliminar el participante");
    }
  };

  const handleView = async (p) => {
    try {
      const res = await participantesService.getParticipante(p.id, userInfo);
      const data = res.data;
      const normalizado = normalizarParticipante(data);
      setSelectedParticipant(normalizado);
      setMode("view");
      setModalOpen(true);
    } catch (error) {
      console.error("Error cargando participante:", error);
      message.error("Error cargando participante");
    }
  };

  const recargarDatos = async () => {
    try {
      const [metricasResponse, graficasResponse] = await Promise.all([
        metricasService.obtenerMetricasParticipantes(userInfo, filtrosActuales),
        graficasService.obtenerDatosGraficas(userInfo, filtrosActuales)
      ]);
      setMetricas(metricasResponse.data);
      setDatosGraficas(graficasResponse.data);
    } catch (error) {
      console.error("Error recargando datos:", error);
    }
  };

  const handleModalClose = async (reload = false, editedId = null) => {
    setModalOpen(false);
    if (reload) {
      await loadParticipants();
      await recargarDatos();
    }
  };

  const handleViewCallHistory = async (p) => {
    try {
      setLoadingHistorial(true);
      const [participantRes, callsRes] = await Promise.all([
        participantesService.getParticipante(p.id, userInfo),
        llamadasService.getLlamadas(p.id)
      ]);
      const data = participantRes.data;
      const normalizado = normalizarParticipante(data);
      normalizado.llamadas = callsRes.data || [];
      setSelectedParticipant(normalizado);
      setSelectedCalls(callsRes.data || []);
      setCallHistoryModalOpen(true);
    } catch (err) {
      console.error("❌ Error cargando historial:", err);
      message.error("Error cargando historial de llamadas");
    } finally {
      setLoadingHistorial(false);
    }
  };

  const handleCallHistoryClose = () => {
    setCallHistoryModalOpen(false);
    setSelectedCalls([]);
    setSelectedParticipant(null);
  };

  const recargarDatosCompletos = useCallback(async () => {
    try {
      const [metricasResponse, graficasResponse] = await Promise.all([
        metricasService.obtenerMetricasParticipantes(userInfo, filtrosActuales),
        graficasService.obtenerDatosGraficas(userInfo, filtrosActuales)
      ]);
      setMetricas(metricasResponse.data);
      setDatosGraficas(graficasResponse.data);
    } catch (error) {
      console.error("❌ Error recargando datos:", error);
      message.error("Error actualizando datos");
    }
  }, [userInfo, filtrosActuales]);

  const handleAddCall = async (p) => {
    try {
      const [participantRes, callsRes] = await Promise.all([
        participantesService.getParticipante(p.id, userInfo),
        llamadasService.getLlamadas(p.id)
      ]);

      const data = participantRes.data;
      const normalizado = normalizarParticipante(data);
      normalizado.llamadas = callsRes.data || [];

      setSelectedParticipant(normalizado);
      setAddCallModalOpen(true);
    } catch (e) {
      console.error("❌ Error cargando participante antes de llamada", e);
      message.error("Error preparando llamada");
    }
  };

  const handleAddCallModalClose = async (shouldReload = false, updatedParticipantId = null) => {
    setAddCallModalOpen(false);
    if (shouldReload) {
      message.loading("Actualizando datos...", 1);
      try {
        await Promise.all([
          loadParticipants(),
          recargarDatosCompletos()
        ]);
        if (updatedParticipantId) {
          try {
            const participantRes = await participantesService.getParticipante(updatedParticipantId, userInfo);
            const updatedParticipant = normalizarParticipante(participantRes.data);
            setParticipants(prev =>
              prev.map(p =>
                p.id === updatedParticipantId ? { ...updatedParticipant, status: updatedParticipant.estado_resumen || 'NO_VERIFICADO' } : p
              )
            );
          } catch (err) {
            console.warn("⚠️ No se pudo actualizar participante individual:", err);
          }
        }
        message.success("Datos actualizados correctamente");
      } catch (error) {
        console.error("❌ Error recargando datos después de llamada:", error);
        message.error("Error actualizando datos");
      }
    }
  };

  const getNextCallNumber = (participant) => {
    if (!participant) return 1;
    if (['VERIFICADO', 'NO_CONTINUA'].includes(participant.estado_validacion)) return null;
    if (participant.total_intentos >= 3) return null;
    return participant.total_intentos + 1;
  };

  const getRealStatus = (p) => {
    const estado = p.estado_resumen || p.estado_validacion;
    switch (estado) {
      case 'VERIFICADO': return 'Verificado';
      case 'NO_CONTINUA': return 'No Continúa';
      case 'EN_PROCESO': return 'En Proceso';
      case 'NO_VERIFICADO':
      default: return 'No Verificado';
    }
  };

  const statusColors = {
    'Verificado': "#009728",
    'En Proceso': "#0968C6",
    'No Continúa': "#b40000",
    'No Verificado': "#b4b4b4",
  };

  const canAddCall = (participant) => {
    if (!participant) return false;
    const nextCall = getNextCallNumber(participant);
    return nextCall !== null && nextCall <= 3;
  };

  const processedParticipants = participants.map(p => {
    const normalizado = normalizarParticipante(p);
    return {
      ...normalizado,
      status: normalizado.estado_resumen || 'NO_VERIFICADO'
    };
  });

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterEstado("todos");
    setFilterSexo("todos");
    setPromotoresId(null);
    if (isAdmin) {
      setFilterDepartamento("todos");
      setFilterMunicipio("todos");
    } else {
      setFilterDepartamento(user.cod_departamento);
      setFilterMunicipio(user.cod_municipio);
    }
    setPage(1);
  };

  const hasActiveFilters = useMemo(() => {
    return searchTerm !== "" ||
      filterEstado !== "todos" ||
      filterSexo !== "todos" ||
      filterDepartamento !== "todos" ||
      filterMunicipio !== "todos";
  }, [searchTerm, filterEstado, filterSexo, filterDepartamento, filterMunicipio]);

  const numberToSpanishOrder = (num) => {
    switch (num) {
      case 1: return "primera";
      case 2: return "segunda";
      case 3: return "tercera";
      default: return `${num}°`;
    }
  };

  const getCallLabel = (participant) => {
    const next = getNextCallNumber(participant);
    if (!next) return "No se pueden agregar más llamadas";
    return `Registrar ${numberToSpanishOrder(next)} llamada`;
  };

  const getAllParticipantsForExport = async () => {
    try {
      const params = {
        search: searchTerm,
        estado: filterEstado !== "todos" ? filterEstado : undefined,
        municipioId: filterMunicipio !== "todos" ? parseInt(filterMunicipio) : undefined,
        departamentoId: filterDepartamento !== "todos" ? parseInt(filterDepartamento) : undefined,
        genero: filterSexo !== "todos" ? filterSexo : undefined,
        promotoresId: promotoresId || undefined,
        exportAll: true
      };
      const response = await participantesService.getParticipantes(params, userInfo);
      return response.data || [];
    } catch (error) {
      message.error("Error obteniendo datos para exportar");
      return [];
    }
  };

  const handleReproceso = (p) => {
    setReprocesoParticipant(p);
    setReprocesoOpen(true);
  };

  const handleReprocesoClose = async (reload = false) => {
    setReprocesoOpen(false);
    setReprocesoParticipant(null);

    if (reload) {
      await loadParticipants();
      await recargarDatosCompletos();
    }
  };

  return (
    <div className="p-4 md:p-6 bg-[#f9fafb] min-h-screen">
      <ParticipantesCharts
        total={total}
        conteoEstados={metricas}
        metricas={metricas}
        estados={maestros.estadosValidacion}
        hasActiveFilters={hasActiveFilters}
      />
      {showCharts && (
        <ChartsSection
          datosGraficas={datosGraficas}
          estadosValidacion={maestros.estadosValidacion}
          loading={loadingGraficas || !graficasCargadas}
        />
      )}
      <ParticipantesFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterEstado={filterEstado}
        setFilterEstado={setFilterEstado}
        filterSexo={filterSexo}
        setFilterSexo={setFilterSexo}
        filterDepartamento={filterDepartamento}
        setFilterDepartamento={setFilterDepartamento}
        filterMunicipio={filterMunicipio}
        setFilterMunicipio={setFilterMunicipio}
        estadosBackend={maestros.estadosValidacion}
        departamentos={maestros.departamentosFiltrados}
        municipios={maestros.municipios}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        permissionInsert={permissionInsert}
        handleCreate={handleCreate}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        promotores={promotores}
        promotoresId={promotoresId}
        setPromotoresId={setPromotoresId}
        loadingPromotores={loadingPromotores}
        isAdmin={isAdmin}
        isRol2={isRol2}
        isRol3={isRol3}
        getAllParticipantsForExport={getAllParticipantsForExport}
      />
      <ParticipantesTable
        participants={processedParticipants}
        loading={loading}
        page={page}
        total={total}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        permissionUpdate={permissionUpdate}
        permissionDelete={permissionDelete}
        handleView={handleView}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleViewCallHistory={handleViewCallHistory}
        handleAddCall={handleAddCall}
        getRealStatus={getRealStatus}
        canAddCall={canAddCall}
        getCallLabel={getCallLabel}
        statusColors={statusColors}
        getNextCallNumber={getNextCallNumber}
        hasActiveFilters={hasActiveFilters}
        handleReproceso={handleReproceso}
        isAdmin={isAdmin}
        isRol2={isRol2}
      />
      {modalOpen && (
        <ParticipantModal
          open={modalOpen}
          onCancel={handleModalClose}
          onSaved={handleModalClose}
          participant={selectedParticipant}
          mode={mode}
          userInfo={userInfo}
          maestros={maestros}
        />
      )}
      {addCallModalOpen && (
        <AddCallModal
          open={addCallModalOpen}
          onClose={handleAddCallModalClose}
          participant={selectedParticipant}
          callNumber={getNextCallNumber(selectedParticipant)}
          previousCalls={selectedParticipant?.llamadas || []}
          userInfo={userInfo}
          motivosNoElegible={maestros.motivosNoElegible}
          estadosValidacion={maestros.estadosValidacion}
          subestadosValidacion={maestros.subestadosValidacion}
        />
      )}
      {callHistoryModalOpen && (
        <CallHistoryModal
          open={callHistoryModalOpen}
          onClose={handleCallHistoryClose}
          participant={selectedParticipant}
          calls={selectedCalls}
          loading={loadingHistorial}
        />
      )}

      {reprocesoOpen && (
        <ReprocesoModal
          open={reprocesoOpen}
          participante={reprocesoParticipant}
          onClose={handleReprocesoClose}
        />
      )}
    </div>
  );
}